import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Alert, Checkbox, Descriptions, Modal, Space, Spin, Switch, Table, Tag, Tooltip, message } from "antd";
import { FilePdfOutlined, SaveOutlined } from "@ant-design/icons";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  ButtonItem,
  InputNumberItem,
  ModalItem,
  SegmentedItem,
  SelectItem,
  cuentaMask,
} from "../../../../Components/Items";
import CuentasEmpleadosConcepto from "./CuentasEmpleadosConcepto";
import ADVERTENCIAS, { TIPO_LABELS, tieneProblema } from "./advertencias";
import verificacionContabilidadNominaPdf from "../../../../Pdf/verificacionContabilidadNominaPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const CAMPOS_EDITABLES = [
  "cuentaCostoGastoId",
  "cuentaPasivoId",
  "generarCuentaPorEmpleado",
  "consecutivoCuentas",
  "conceptoContableId",
  "esPrestamoPercepcion",
  "conceptoCobroPrestamoId",
];

const mismoValor = (a, b) => {
  if (a == null && b == null) return true;
  if (typeof a === "boolean" || typeof b === "boolean") return !!a === !!b;
  if (typeof a === "number" || typeof b === "number") return Number(a) === Number(b);
  return String(a ?? "") === String(b ?? "");
};

class VerificarContabilidadNomina extends Component {
  state = {
    loading: false,
    guardando: false,
    generandoPdf: false,
    conceptos: [],
    configuracionGeneral: null,
    opcionesCuentas: [],
    cuentasPorId: {},
    cambios: {},
    filtroTipo: 0,
    soloProblemas: false,
    expandedRowKeys: [],
    // Concepto cuyo impacto de agrupación se está consultando (pinta el spinner del select).
    consultandoImpacto: null,
    // Conceptos sin "Generar cuenta por empleado" donde el usuario pidió ver de
    // todos modos las cuentas que quedaron de una configuración anterior.
    verEmpleadosSinGenerar: {},
    modalPdf: false,
    incluirAnexoEmpleados: true,
  };

  componentDidMount() {
    this.consultarDatos();
    this.consultarCuentas();
    window.addEventListener("beforeunload", this.avisarCambiosPendientes);
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.avisarCambiosPendientes);
  }

  avisarCambiosPendientes = (e) => {
    if (!Object.keys(this.state.cambios).length) return undefined;
    e.preventDefault();
    e.returnValue = "";
    return "";
  };

  consultarDatos() {
    this.setState({ loading: true });
    axios
      .post(
        "nomina/verificarContabilidad",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          this.setState({
            conceptos: res.data.conceptos || [],
            configuracionGeneral: res.data.configuracionGeneral || null,
            cambios: {},
          });
        } else {
          message.error(res.data.message || "No se pudo cargar la verificación de contabilidad");
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error("Error al comunicarse con el servidor");
      });
  }

  // selectTodasCuentasContables, no selectCuentasContablesFinales: este último sólo
  // devuelve cuentas hoja, y una cuenta de pasivo con «Generar cuenta por empleado»
  // deja de serlo en cuanto genera su primera subcuenta.
  consultarCuentas() {
    axios
      .post(
        "cuentascontables/selectTodasCuentasContables",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (!Array.isArray(res.data)) return;
        const opcionesCuentas = [];
        const cuentasPorId = {};
        res.data.filter(Boolean).forEach((cuenta) => {
          const codigo =
            (cuenta.nivel1 || "") + (cuenta.nivel2 || "") + (cuenta.nivel3 || "") +
            (cuenta.nivel4 || "") + (cuenta.nivel5 || "");
          // Las opciones se quedan sólo con value/label: cualquier prop extra la
          // reenvía rc-select al DOM y React se queja de atributos desconocidos.
          opcionesCuentas.push({ value: cuenta._id, label: `${cuentaMask(codigo)} - ${cuenta.descripcion}` });
          cuentasPorId[cuenta._id] = { codigo, descripcion: cuenta.descripcion };
        });
        this.setState({ opcionesCuentas, cuentasPorId });
      })
      .catch(() => message.error("Error al cargar el catálogo de cuentas contables"));
  }

  valorActual(record, campo) {
    const cambio = this.state.cambios[record._id];
    if (cambio && campo in cambio) return cambio[campo];
    return record[campo];
  }

  setCambio(record, campo, valor) {
    this.setState((prev) => {
      const anterior = prev.cambios[record._id] || {};
      const nuevo = { ...anterior, [campo]: valor };

      const sigueDistinto = CAMPOS_EDITABLES.some(
        (c) => c in nuevo && !mismoValor(nuevo[c], record[c])
      );

      const cambios = { ...prev.cambios };
      if (sigueDistinto) cambios[record._id] = nuevo;
      else delete cambios[record._id];

      return { cambios };
    });
  }

  // ── «Se contabiliza en» ───────────────────────────────────────────────────
  // Sólo se puede agrupar en un concepto del mismo tipo, que no sea informativo y que no
  // esté agrupado a su vez (un solo nivel). Se mira el valor sin guardar, para que la lista
  // reaccione a lo que acabas de elegir en otro renglón.
  opcionesConceptoContable(record) {
    const etiqueta = (c) =>
      `${c.numeroConcepto != null ? c.numeroConcepto : c.numero} - ${c.descripcion}`;

    const opciones = this.state.conceptos
      .filter(
        (c) =>
          c._id !== record._id &&
          c.tipo === record.tipo &&
          !c.noSuma &&
          !this.valorActual(c, "conceptoContableId")
      )
      .map((c) => ({ value: c._id, label: etiqueta(c) }));

    // El concepto ya configurado puede haber dejado de cumplir los filtros (lo volvieron
    // informativo, lo agruparon a su vez, lo borraron). Se agrega para que el Select no
    // enseñe un ObjectId pelón, y la advertencia del renglón explica el problema.
    const actual = this.valorActual(record, "conceptoContableId");
    if (actual && !opciones.some((o) => o.value === actual)) {
      const destino = this.state.conceptos.find((c) => c._id === actual);
      opciones.unshift({
        value: actual,
        label: destino ? etiqueta(destino) : "Concepto no encontrado",
      });
    }

    return opciones;
  }

  nombreConceptoContable(record) {
    const id = this.valorActual(record, "conceptoContableId");
    if (!id) return null;
    const destino = this.state.conceptos.find((c) => c._id === id);
    return destino ? destino.descripcion : record.conceptoContable?.descripcion || null;
  }

  // ── «Préstamo entregado en la nómina» ─────────────────────────────────────
  // Sólo puede cobrarlo una deducción que no sea informativa y que tenga cuenta de pasivo: de
  // esa cuenta sale la cuenta por cobrar del empleado. Se mira el valor sin guardar, para que la
  // lista reaccione a lo que acabas de cambiar en el renglón de la deducción.
  opcionesConceptoCobro(record) {
    const etiqueta = (c) =>
      `${c.numeroConcepto != null ? c.numeroConcepto : c.numero} - ${c.descripcion}`;

    const opciones = this.state.conceptos
      .filter(
        (c) =>
          c._id !== record._id &&
          c.tipo === 2 &&
          !c.noSuma &&
          this.valorActual(c, "cuentaPasivoId")
      )
      .map((c) => ({ value: c._id, label: etiqueta(c) }));

    // El concepto ya configurado puede haber dejado de cumplir los filtros (le quitaron la
    // cuenta, lo volvieron informativo, lo borraron). Se agrega para que el Select no enseñe un
    // ObjectId pelón, y la advertencia del renglón explica el problema.
    const actual = this.valorActual(record, "conceptoCobroPrestamoId");
    if (actual && !opciones.some((o) => o.value === actual)) {
      const destino = this.state.conceptos.find((c) => c._id === actual);
      opciones.unshift({
        value: actual,
        label: destino ? etiqueta(destino) : "Concepto no encontrado",
      });
    }

    return opciones;
  }

  // Apagar la bandera tiene que limpiar el enlace: el backend no guarda uno sin la otra, y
  // dejarlo en pantalla haría creer que sigue configurado.
  cambiarEsPrestamo(record, valor) {
    this.setCambio(record, "esPrestamoPercepcion", valor);
    if (!valor) this.setCambio(record, "conceptoCobroPrestamoId", null);
  }

  // De qué cuenta hereda el cargo una entrega de préstamo. Null cuando no hay enlace usable:
  // entonces el concepto usa su propia cuenta y la advertencia del renglón lo dice.
  cuentaPrestamoHeredada(record) {
    if (!this.valorActual(record, "esPrestamoPercepcion")) return null;
    const id = this.valorActual(record, "conceptoCobroPrestamoId");
    if (!id) return null;
    // Con un cambio sin guardar el backend todavía no resolvió la cuenta: se saca del renglón de
    // la deducción, que es la misma fuente que usará al recargar.
    const cobro = this.state.conceptos.find((c) => c._id === id);
    if (!cobro || cobro.tipo !== 2) return null;
    const cuentaId = this.valorActual(cobro, "cuentaPasivoId");
    if (!cuentaId) return null;
    const cuenta = this.state.cuentasPorId[cuentaId];
    return {
      concepto: cobro.descripcion,
      etiqueta: cuenta ? `${cuentaMask(cuenta.codigo)} - ${cuenta.descripcion}` : null,
      generarCuentaPorEmpleado: !!this.valorActual(cobro, "generarCuentaPorEmpleado"),
    };
  }

  // Antes de agrupar se consulta qué se lleva por delante: la póliza es append-only y la
  // cancelación de un CFDI recalcula los movimientos con la configuración vigente, así que
  // agrupar un concepto que ya se timbró cambia cómo se revierte lo viejo. Y si ya tenía
  // subcuentas por empleado, ésas se quedan con su saldo sin que nadie las alimente.
  elegirConceptoContable(record, valor) {
    if (!valor) {
      this.setCambio(record, "conceptoContableId", null);
      return;
    }

    const aplicar = () => this.setCambio(record, "conceptoContableId", valor);

    this.setState({ consultandoImpacto: record._id });
    axios
      .post(
        "nomina/impactoAgruparConcepto",
        { empresaId: this.props.empresaId, conceptoNominaId: record._id },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ consultandoImpacto: null });
        if (res.data.success !== 2) return aplicar();

        const { tieneTimbrado, subcuentasAsignadas } = res.data;
        const avisos = [];
        if (tieneTimbrado) {
          avisos.push(
            "Este concepto ya salió en nóminas timbradas. La agrupación rige para los timbrados futuros: las pólizas ya generadas no se tocan, pero si más adelante cancelas un CFDI de esas nóminas, la reversa se va a calcular con la agrupación nueva."
          );
        }
        if (subcuentasAsignadas > 0) {
          avisos.push(
            `${subcuentasAsignadas} empleado(s) tienen subcuenta contable de este concepto. A partir de ahora se usan las del concepto principal, y esas cuentas se quedan con el saldo que traigan sin recibir movimientos nuevos.`
          );
        }

        if (!avisos.length) return aplicar();

        Modal.confirm({
          title: `Contabilizar «${record.descripcion}» dentro de otro concepto`,
          width: 620,
          content: (
            <div>
              {avisos.map((aviso, i) => (
                <p key={i} style={{ marginBottom: 8 }}>{aviso}</p>
              ))}
            </div>
          ),
          okText: "Agrupar de todos modos",
          cancelText: "Cancelar",
          onOk: aplicar,
        });
      })
      .catch(() => {
        // Si la consulta falla no se bloquea la configuración: se aplica y se avisa.
        this.setState({ consultandoImpacto: null });
        message.warning("No se pudo revisar el impacto del cambio; revísalo antes de guardar");
        aplicar();
      });
  }

  // El hijo avisa cuando un empleado gana o pierde asignación, para no tener que
  // recargar toda la pestaña (y perder los cambios sin guardar).
  ajustarConteo = (conceptoId, delta) => {
    this.setState((prev) => ({
      conceptos: prev.conceptos.map((c) => {
        if (c._id !== conceptoId) return c;
        const empleadosConCuenta = Math.max(0, (c.empleadosConCuenta || 0) + delta);
        const faltan =
          !c.heredaContabilidad &&
          c.generarCuentaPorEmpleado && c.cuentaPasivoId && c.empleadosAplican > empleadosConCuenta;
        const advertencias = c.advertencias.filter((a) => a !== "EMPLEADOS_SIN_CUENTA");
        if (faltan) advertencias.push("EMPLEADOS_SIN_CUENTA");
        return { ...c, empleadosConCuenta, advertencias };
      }),
    }));
  };

  guardarCambios() {
    const { cambios, conceptos } = this.state;
    const ids = Object.keys(cambios);
    if (!ids.length) return;

    const payload = ids
      .map((id) => {
        const record = conceptos.find((c) => c._id === id);
        if (!record) return null;
        return {
          conceptoNominaId: id,
          cuentaCostoGastoId: this.valorActual(record, "cuentaCostoGastoId") || null,
          cuentaPasivoId: this.valorActual(record, "cuentaPasivoId") || null,
          generarCuentaPorEmpleado: !!this.valorActual(record, "generarCuentaPorEmpleado"),
          consecutivoCuentas: this.valorActual(record, "consecutivoCuentas"),
          conceptoContableId: this.valorActual(record, "conceptoContableId") || null,
          esPrestamoPercepcion: !!this.valorActual(record, "esPrestamoPercepcion"),
          conceptoCobroPrestamoId: this.valorActual(record, "conceptoCobroPrestamoId") || null,
        };
      })
      .filter(Boolean);

    this.setState({ guardando: true });
    axios
      .post(
        "nomina/editarContabilidadConceptos",
        { empresaId: this.props.empresaId, conceptos: payload },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ guardando: false });
        if (res.data.success === 2) {
          message.success(res.data.message || "Configuración contable actualizada");
          this.consultarDatos();
        } else {
          message.error(res.data.message || "No se pudo guardar la configuración");
        }
      })
      .catch(() => {
        this.setState({ guardando: false });
        message.error("Error al comunicarse con el servidor");
      });
  }

  descargarPdf() {
    this.setState({ generandoPdf: true });
    axios
      .post(
        "nomina/datosPdfContabilidadNomina",
        {
          empresaId: this.props.empresaId,
          incluirAnexoEmpleados: this.state.incluirAnexoEmpleados,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ generandoPdf: false });
        if (res.data.success !== 2) {
          message.error(res.data.message || "No se pudieron obtener los datos del PDF");
          return;
        }
        pdfMake
          .createPdf(verificacionContabilidadNominaPdf(res.data))
          .download("Configuracion_Contable_Nomina.pdf");
        this.setState({ modalPdf: false });
      })
      .catch(() => {
        this.setState({ generandoPdf: false });
        message.error("Error al comunicarse con el servidor");
      });
  }

  conceptosFiltrados() {
    const { conceptos, filtroTipo, soloProblemas } = this.state;
    return conceptos.filter((c) => {
      if (filtroTipo && c.tipo !== filtroTipo) return false;
      if (soloProblemas && !tieneProblema(c.advertencias)) return false;
      return true;
    });
  }

  // Sólo los conceptos con "Generar cuenta por empleado" tienen cuentas por empleado
  // que ver. Se decide con el valor guardado, no con el que esté sin guardar en la
  // tabla: las subcuentas viven en el empleado y no cambian hasta que se timbra.
  renderFilaExpandida(record) {
    const { cuentasPorId, opcionesCuentas, verEmpleadosSinGenerar } = this.state;
    const forzado = !!verEmpleadosSinGenerar[record._id];

    // Agrupado: la subcuenta que se usa es la del concepto principal, así que las de aquí
    // —si quedaron de antes— ya no reciben movimientos.
    const heredaGuardado = !!record.conceptoContable;
    if (heredaGuardado && !forzado) {
      const conCuenta = record.empleadosConCuenta || 0;
      return (
        <Alert
          type="info"
          showIcon
          message={`Se contabiliza en «${record.conceptoContable.descripcion}»`}
          description={
            <span>
              Los importes de este concepto se suman a los de{" "}
              <b>{record.conceptoContable.descripcion}</b> y salen en un solo movimiento por
              proyecto, con sus cuentas y su texto. Si ese concepto genera cuenta por empleado, se
              usa la subcuenta de <b>allá</b>, no una de aquí.
              {conCuenta > 0 && (
                <div style={{ marginTop: 8 }}>
                  <b>{conCuenta}</b> empleado(s) conservan una subcuenta de este concepto, de cuando
                  se contabilizaba solo. Ya no reciben movimientos y se quedan con el saldo que
                  traigan; si no es cero hay que traspasarlo a mano.
                  <div style={{ marginTop: 6 }}>
                    <ButtonItem
                      type="default"
                      size="small"
                      onClick={() =>
                        this.setState((prev) => ({
                          verEmpleadosSinGenerar: { ...prev.verEmpleadosSinGenerar, [record._id]: true },
                        }))
                      }
                    >
                      Ver esas cuentas
                    </ButtonItem>
                  </div>
                </div>
              )}
            </span>
          }
        />
      );
    }

    // Una entrega de préstamo sí tiene seguimiento por empleado, pero la subcuenta no es suya:
    // es la del concepto que cobra las amortizaciones, y es donde caen el cargo de la entrega y
    // los abonos del cobro. Sin este aviso el renglón se leería como "no abre por empleado".
    //
    // Se salta cuando el concepto además abre subcuentas propias del lado del pasivo: ahí sí hay
    // una lista real que mostrar y taparla con el aviso sería peor.
    if (record.conceptoCobroPrestamo && !record.generarCuentaPorEmpleado && !forzado) {
      const cobro = record.conceptoCobroPrestamo;
      const conCuenta = record.empleadosConCuenta || 0;
      return (
        <Alert
          type="info"
          showIcon
          message={`El préstamo lo cobra «${cobro.descripcion}»`}
          description={
            <span>
              El cargo de la entrega va a la cuenta por cobrar de ese concepto
              {cobro.cuentaPorCobrar ? (
                <>
                  {" "}(<b>{cuentaMask(cobro.cuentaPorCobrar.codigo)} - {cobro.cuentaPorCobrar.descripcion}</b>)
                </>
              ) : null}
              , al proyecto general y sin traspaso intercentro.{" "}
              {cobro.generarCuentaPorEmpleado ? (
                <>
                  Se usa la <b>misma subcuenta por empleado</b> que los abonos del cobro, así que el
                  saldo del préstamo de cada persona se lee directo de esa cuenta. Para verlas o
                  editarlas, expande el renglón de <b>{cobro.descripcion}</b>.
                </>
              ) : (
                <>
                  Ese concepto no genera cuenta por empleado, así que la entrega y los cobros caen en
                  la cuenta mayor: la póliza cuadra, pero no se puede leer cuánto debe cada persona.
                </>
              )}
              {conCuenta > 0 && (
                <div style={{ marginTop: 8 }}>
                  <b>{conCuenta}</b> empleado(s) conservan una subcuenta de este concepto, de una
                  configuración anterior. Ya no reciben movimientos y se quedan con el saldo que
                  traigan; si no es cero hay que traspasarlo a mano.
                  <div style={{ marginTop: 6 }}>
                    <ButtonItem
                      type="default"
                      size="small"
                      onClick={() =>
                        this.setState((prev) => ({
                          verEmpleadosSinGenerar: { ...prev.verEmpleadosSinGenerar, [record._id]: true },
                        }))
                      }
                    >
                      Ver esas cuentas
                    </ButtonItem>
                  </div>
                </div>
              )}
            </span>
          }
        />
      );
    }

    if (!record.generarCuentaPorEmpleado && !forzado) {
      const encendidoSinGuardar = !!this.valorActual(record, "generarCuentaPorEmpleado");
      const conCuenta = record.empleadosConCuenta || 0;

      return (
        <Alert
          type="info"
          showIcon
          message="Este concepto no abre por empleado"
          description={
            <span>
              {record.noSuma ? (
                <>
                  Es un concepto <b>informativo</b> (tiene encendido «No Suma»): al timbrar se
                  descarta antes de revisar cuentas, así que no genera ningún movimiento contable ni
                  captura cuentas. Por lo mismo no hay subcuentas por empleado que mostrar.
                </>
              ) : encendidoSinGuardar ? (
                <>
                  Acabas de encender <b>«Genera cuenta por empleado»</b>, pero el cambio todavía no
                  se guarda. Presiona <b>Guardar cambios</b> para poder ver y editar la cuenta de
                  cada empleado.
                </>
              ) : (
                <>
                  No está configurado para generar cuenta por empleado: todos los empleados abonan
                  directamente a la cuenta de pasivo del concepto, así que no hay subcuentas
                  individuales que mostrar. Enciende <b>«Genera cuenta por empleado»</b> en la
                  columna correspondiente y guarda los cambios si quieres que se generen.
                </>
              )}
              {conCuenta > 0 && (
                <div style={{ marginTop: 8 }}>
                  Aun así, <b>{conCuenta}</b> empleado(s) conservan una cuenta asignada de una
                  configuración anterior. Esas cuentas ya no se usan al timbrar.
                  <div style={{ marginTop: 6 }}>
                    <ButtonItem
                      type="default"
                      size="small"
                      onClick={() =>
                        this.setState((prev) => ({
                          verEmpleadosSinGenerar: { ...prev.verEmpleadosSinGenerar, [record._id]: true },
                        }))
                      }
                    >
                      Ver esas cuentas
                    </ButtonItem>
                  </div>
                </div>
              )}
            </span>
          }
        />
      );
    }

    return (
      <CuentasEmpleadosConcepto
        conceptoNominaId={record._id}
        descripcion={record.descripcion}
        opcionesCuentas={opcionesCuentas}
        cuentasPorId={cuentasPorId}
        soloConCuenta={!record.generarCuentaPorEmpleado}
        informativo={!!record.noSuma}
        onAsignacionCambiada={this.ajustarConteo}
      />
    );
  }

  columnas() {
    const { opcionesCuentas, cambios } = this.state;

    const filtroCuenta = (input, option) =>
      (option?.label || "").toLowerCase().includes(input.toLowerCase());

    return [
      {
        title: "N°",
        key: "numero",
        width: 60,
        fixed: "left",
        render: (_, record) => (record.numeroConcepto != null ? record.numeroConcepto : record.numero),
      },
      {
        title: "Descripción",
        dataIndex: "descripcion",
        key: "descripcion",
        width: 200,
        fixed: "left",
        render: (value, record) => (
          <span>
            {value}
            {record.noSuma && (
              <Tooltip title="Concepto informativo («No Suma»): no genera movimientos contables.">
                <Tag color="purple" style={{ marginLeft: 6 }}>Informativo</Tag>
              </Tooltip>
            )}
            {record.estado === 2 && (
              <Tag color="orange" style={{ marginLeft: 6 }}>Deshabilitado</Tag>
            )}
          </span>
        ),
      },
      {
        title: "Tipo",
        dataIndex: "tipo",
        key: "tipo",
        width: 110,
        render: (value) => {
          const t = TIPO_LABELS[value];
          return t ? <Tag color={t.color}>{t.label}</Tag> : "—";
        },
      },
      {
        title: "Se contabiliza en",
        key: "conceptoContableId",
        width: 240,
        render: (_, record) => {
          // Un concepto que ya absorbe a otros no puede volverse hijo: sería una cadena, y
          // sólo se permite un nivel.
          if (record.noSuma) return <span style={{ color: "#bfbfbf" }}>—</span>;
          if (record.conceptosAbsorbidos > 0) {
            return (
              <Tooltip
                title={`${record.conceptosAbsorbidos} concepto(s) se contabilizan aquí, así que éste no puede agruparse en otro. Quítales la agrupación primero.`}
              >
                <Tag color="cyan">Absorbe {record.conceptosAbsorbidos}</Tag>
              </Tooltip>
            );
          }
          return (
            <SelectItem
              value={this.valorActual(record, "conceptoContableId") || undefined}
              options={this.opcionesConceptoContable(record)}
              placeholder="Con sus propias cuentas"
              loading={this.state.consultandoImpacto === record._id}
              allowClear
              showSearch
              noFocusSiguiente
              filterOption={filtroCuenta}
              style={{ width: "100%" }}
              onChange={(valor) => this.elegirConceptoContable(record, valor || null)}
            />
          );
        },
      },
      {
        // El préstamo que se entrega dentro de la nómina carga a la cuenta por cobrar del
        // empleado —la del concepto que cobra las amortizaciones—, no a una cuenta de gasto.
        // Ligar los dos conceptos es lo que hace que el cargo de la entrega y los abonos del
        // cobro caigan en la misma subcuenta y el saldo se lea directo.
        title: "Préstamo: cobra en",
        key: "conceptoCobroPrestamoId",
        width: 250,
        render: (_, record) => {
          if (record.noSuma || record.tipo !== 1 || this.nombreConceptoContable(record)) {
            return <span style={{ color: "#bfbfbf" }}>—</span>;
          }
          const esPrestamo = !!this.valorActual(record, "esPrestamoPercepcion");
          return (
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Tooltip title="El concepto entrega un préstamo al empleado dentro de la nómina: sube el neto a pagar y su cargo va a la cuenta por cobrar del empleado, al proyecto general y sin traspaso intercentro.">
                <Switch
                  size="small"
                  checked={esPrestamo}
                  onChange={(valor) => this.cambiarEsPrestamo(record, valor)}
                />
              </Tooltip>
              {esPrestamo && (
                <SelectItem
                  value={this.valorActual(record, "conceptoCobroPrestamoId") || undefined}
                  options={this.opcionesConceptoCobro(record)}
                  placeholder="¿Qué deducción lo cobra?"
                  allowClear
                  showSearch
                  noFocusSiguiente
                  filterOption={filtroCuenta}
                  style={{ width: "100%" }}
                  onChange={(valor) => this.setCambio(record, "conceptoCobroPrestamoId", valor || null)}
                />
              )}
            </Space>
          );
        },
      },
      {
        title: "Cuenta de Costo o Gasto",
        key: "cuentaCostoGastoId",
        width: 280,
        render: (_, record) => {
          const hereda = this.nombreConceptoContable(record);
          const prestamo = this.cuentaPrestamoHeredada(record);
          return (
            <SelectItem
              value={
                hereda || prestamo
                  ? undefined
                  : this.valorActual(record, "cuentaCostoGastoId") || undefined
              }
              options={opcionesCuentas}
              placeholder={
                hereda
                  ? `Hereda de ${hereda}`
                  : prestamo
                  ? `Por cobrar: ${prestamo.etiqueta || prestamo.concepto}`
                  : record.noSuma
                  ? "No aplica (informativo)"
                  : record.tipo === 2
                  ? "No aplica en deducciones"
                  : "Sin cuenta"
              }
              disabled={!!hereda || !!prestamo || record.noSuma || record.tipo === 2}
              allowClear
              showSearch
              noFocusSiguiente
              filterOption={filtroCuenta}
              style={{ width: "100%" }}
              onChange={(valor) => this.setCambio(record, "cuentaCostoGastoId", valor || null)}
            />
          );
        },
      },
      {
        title: "Cuenta de Pasivo",
        key: "cuentaPasivoId",
        width: 280,
        render: (_, record) => {
          const hereda = this.nombreConceptoContable(record);
          return (
            <SelectItem
              value={hereda ? undefined : this.valorActual(record, "cuentaPasivoId") || undefined}
              options={opcionesCuentas}
              placeholder={
                hereda ? `Hereda de ${hereda}` : record.noSuma ? "No aplica (informativo)" : "Sin cuenta"
              }
              disabled={!!hereda || record.noSuma}
              allowClear
              showSearch
              noFocusSiguiente
              filterOption={filtroCuenta}
              style={{ width: "100%" }}
              onChange={(valor) => this.setCambio(record, "cuentaPasivoId", valor || null)}
            />
          );
        },
      },
      {
        title: "Genera cuenta por empleado",
        key: "generarCuentaPorEmpleado",
        width: 140,
        align: "center",
        render: (_, record) =>
          record.noSuma || this.nombreConceptoContable(record) ? (
            <span style={{ color: "#bfbfbf" }}>—</span>
          ) : (
            <Switch
              checked={!!this.valorActual(record, "generarCuentaPorEmpleado")}
              onChange={(valor) => this.setCambio(record, "generarCuentaPorEmpleado", valor)}
            />
          ),
      },
      {
        title: "Consecutivo",
        key: "consecutivoCuentas",
        width: 110,
        render: (_, record) =>
          record.noSuma || this.nombreConceptoContable(record) ? (
            <span style={{ color: "#bfbfbf" }}>—</span>
          ) : (
            <InputNumberItem
              min={1}
              value={this.valorActual(record, "consecutivoCuentas")}
              disabled={!this.valorActual(record, "generarCuentaPorEmpleado")}
              style={{ width: "100%" }}
              onChange={(valor) => this.setCambio(record, "consecutivoCuentas", valor)}
            />
          ),
      },
      {
        title: "Ejemplo que crearía",
        key: "ejemploCuenta",
        width: 260,
        render: (_, record) => {
          if (this.nombreConceptoContable(record)) {
            return <span style={{ color: "#bfbfbf" }}>—</span>;
          }
          // Una entrega de préstamo no abre subcuenta propia: usa la del concepto de cobro, y el
          // ejemplo de esa cuenta se ve en el renglón de la deducción.
          const prestamo = this.cuentaPrestamoHeredada(record);
          if (prestamo?.generarCuentaPorEmpleado) {
            return (
              <span style={{ color: "#595959" }}>
                Usa la subcuenta de «{prestamo.concepto}»
              </span>
            );
          }
          if (!this.valorActual(record, "generarCuentaPorEmpleado")) {
            return <span style={{ color: "#bfbfbf" }}>—</span>;
          }
          const pendiente = !!cambios[record._id];
          if (!record.ejemploCuenta) {
            return (
              <span style={{ color: "#bfbfbf" }}>
                {pendiente ? "Se calcula al guardar" : "—"}
              </span>
            );
          }
          return (
            <span style={{ color: "#595959" }}>
              {cuentaMask(record.ejemploCuenta.codigo)} - {record.ejemploCuenta.descripcion}
              {pendiente && (
                <Tooltip title="Tienes cambios sin guardar en este concepto; el ejemplo se recalcula al guardar.">
                  <Tag color="blue" style={{ marginLeft: 6 }}>Sin guardar</Tag>
                </Tooltip>
              )}
            </span>
          );
        },
      },
      {
        title: "Empleados",
        key: "empleados",
        width: 110,
        align: "center",
        render: (_, record) => {
          if (!record.generarCuentaPorEmpleado) return <span style={{ color: "#bfbfbf" }}>—</span>;
          const faltan = record.empleadosAplican - record.empleadosConCuenta;
          return (
            <Tooltip title={`${record.empleadosConCuenta} con cuenta de ${record.empleadosAplican} a los que aplica el concepto`}>
              <span style={{ color: faltan > 0 ? "#d4380d" : "#389e0d" }}>
                {record.empleadosConCuenta} / {record.empleadosAplican}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: "Estatus",
        key: "advertencias",
        width: 240,
        render: (_, record) => {
          if (!record.advertencias.length) return <Tag color="green">Correcto</Tag>;
          return (
            <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {record.advertencias.map((clave) => {
                const a = ADVERTENCIAS[clave];
                if (!a) return null;
                // El detector de préstamos sin marcar ya sabe con qué deducción hay que ligarlo:
                // nombrarla ahorra tener que adivinarlo.
                const detalle =
                  clave === "PRESTAMO_NO_MARCADO" && record.prestamoSugerido
                    ? `${a.detalle} En este caso, esa deducción es «${record.prestamoSugerido.descripcion}».`
                    : a.detalle;
                return (
                  <Tooltip key={clave} title={detalle}>
                    <Tag color={a.color} style={{ marginRight: 0 }}>{a.texto}</Tag>
                  </Tooltip>
                );
              })}
            </span>
          );
        },
      },
    ];
  }

  render() {
    const {
      cambios, configuracionGeneral, expandedRowKeys, filtroTipo,
      generandoPdf, guardando, loading, soloProblemas,
    } = this.state;

    const totalCambios = Object.keys(cambios).length;
    const filtrados = this.conceptosFiltrados();
    const conProblemas = this.state.conceptos.filter((c) => tieneProblema(c.advertencias)).length;

    return (
      <Spin spinning={loading}>
        {configuracionGeneral && (
          <Descriptions
            bordered
            size="small"
            column={window.innerWidth > 1000 ? 3 : 1}
            style={{ marginBottom: 12, backgroundColor: "white" }}
          >
            <Descriptions.Item label="Tipo de Póliza">
              {configuracionGeneral.tipoDePolizaNombre || <Tag color="red">Sin configurar</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Proyecto General">
              {configuracionGeneral.proyectoNombre || <Tag color="red">Sin configurar</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Póliza por empleado">
              {configuracionGeneral.polizaPorEmpleado ? "Sí" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Movimientos de obligaciones">
              {configuracionGeneral.desactivarMovimientosObligaciones ? (
                <Tag color="orange">Desactivados</Tag>
              ) : (
                "Activos"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Cuenta Intercentro" span={2}>
              {configuracionGeneral.cuentaIntercentro
                ? `${cuentaMask(configuracionGeneral.cuentaIntercentro.codigo)} - ${configuracionGeneral.cuentaIntercentro.descripcion}`
                : "Sin configurar"}
            </Descriptions.Item>
          </Descriptions>
        )}

        {totalCambios > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message={`Tienes ${totalCambios} concepto(s) con cambios sin guardar.`}
            description="Los cambios se aplican hasta que presiones «Guardar cambios»."
          />
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <Space wrap>
            <SegmentedItem
              options={[
                { label: "Todos", value: 0 },
                { label: "Percepciones", value: 1 },
                { label: "Deducciones", value: 2 },
                { label: "Obligaciones", value: 3 },
              ]}
              value={filtroTipo}
              onChange={(value) => this.setState({ filtroTipo: value })}
            />
            <span>
              <Switch
                size="small"
                checked={soloProblemas}
                onChange={(value) => this.setState({ soloProblemas: value })}
              />
              <span style={{ marginLeft: 6 }}>
                Sólo con problemas {conProblemas > 0 && <Tag color="red">{conProblemas}</Tag>}
              </span>
            </span>
          </Space>

          <Space wrap>
            <ButtonItem
              type="default"
              icon={<FilePdfOutlined />}
              onClick={() => this.setState({ modalPdf: true })}
            >
              Imprimir PDF
            </ButtonItem>
            <ButtonItem
              type="primary"
              icon={<SaveOutlined />}
              loading={guardando}
              disabled={totalCambios === 0}
              onClick={this.guardarCambios.bind(this)}
            >
              {totalCambios > 0 ? `Guardar cambios (${totalCambios})` : "Guardar cambios"}
            </ButtonItem>
          </Space>
        </div>

        <Table
          className="custom-small-table"
          size="small"
          rowKey={(record) => record._id}
          columns={this.columnas()}
          dataSource={filtrados}
          locale={{ emptyText: "No hay conceptos que mostrar" }}
          scroll={{ x: 2390, y: "58vh" }}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} conceptos`,
          }}
          rowClassName={(record, index) => (index % 2 === 0 ? "zebra-gris" : "default")}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) => this.setState({ expandedRowKeys: keys }),
            expandedRowRender: (record) => this.renderFilaExpandida(record),
          }}
        />

        <ModalItem
          title="Imprimir configuración contable"
          visible={this.state.modalPdf}
          spinning={generandoPdf}
          onCancel={() => this.setState({ modalPdf: false })}
          onOk={this.descargarPdf.bind(this)}
          okText="Descargar PDF"
          footer
          width={520}
        >
          <div style={{ paddingTop: 8 }}>
            <Checkbox
              checked={this.state.incluirAnexoEmpleados}
              onChange={(e) => this.setState({ incluirAnexoEmpleados: e.target.checked })}
            >
              Incluir anexo de cuentas por empleado
            </Checkbox>
            <div style={{ color: "#8c8c8c", marginTop: 8, fontSize: 12 }}>
              El anexo agrega, por cada concepto que genera cuenta por empleado, la lista de
              empleados con la cuenta contable que tienen asignada. En empresas con muchos empleados
              el PDF puede quedar bastante largo.
            </div>
            <div style={{ color: "#8c8c8c", marginTop: 8, fontSize: 12 }}>
              El PDF se genera con la información guardada en el servidor: si tienes cambios sin
              guardar, guárdalos antes de imprimir.
            </div>
          </div>
        </ModalItem>
      </Spin>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(VerificarContabilidadNomina);
